import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import request from '@/modules/base/util/request'

import settingsConfig from '@/modules/settings/config'
import SelectInput from '@/modules/base/components/inputs/select-input/select-input'
import lang from '@/lang'
import { useFormikContext } from 'formik'
import { makeStyles } from '@mui/styles'

import { toast } from '@/modules/base/components/toast/toastConfig'
import { components } from 'react-select'
import TextInput from '@/modules/base/components/inputs/text-input/text-input'

const useStyles = makeStyles((theme) => ({
  menuListButton: {
    color: theme.primary,
    padding: '8px 12px',
    cursor: 'pointer',
    fontWeight: 600,
    borderBottom: '1px solid',
  },
}))

const EMPTY_CATEGORY = 'Keine Angabe'

const CustomerCategoryInput = (props) => {
  const { value: propValue, onChange, readOnly, useFormik, dataQsId } = props

  const [categories, setCategories] = useState([])
  const [value, setValue] = useState()
  const [isManualEntry, setIsManualEntry] = useState(false)
  const [manualValue, setManualValue] = useState('')

  const classes = useStyles()

  const formikContext = useFormik === undefined || useFormik ? useFormikContext() : null

  useEffect(() => {
    setValue(propValue || EMPTY_CATEGORY)
  }, [propValue])

  const mapCategoriesToSelectOptions = (categories) => {
    return categories.map(category => ({ name: category }))
  }

  const getCategories = (search) => {
    return new Promise((resolve, reject) => {
      request(settingsConfig.miscellaneousData, { auth: true, isConcurrent: true })
        .then((response) => {
          const { body: { data: { customerCategories: categories } } } = response

          const categorySelectOptions = mapCategoriesToSelectOptions(categories)
          setCategories(categories)
          if (search) {
            return resolve(categorySelectOptions.filter(category => category.name.toLowerCase().includes(search.toLowerCase())))
          }
          return resolve(categorySelectOptions)
        })
        .catch(error => reject(error))
    })
  }

  const getCategorySelectProps = () => {
    const name = 'category'
    const fieldMeta = formikContext?.getFieldMeta(name) || null
    const categoryValue = fieldMeta ? fieldMeta.value : value

    return {
      label: lang.tagForm.customerCategories.type,
      name,
      allowCreate: true,
      notAsync: false,
      customSelectOptions: {
        placeholder: lang.selectInput.createPlaceholder,
        valueKey: 'name',
        labelKey: 'name',
        isClearable: false,
        loadOptions: getCategories,
        defaultOptions: true,
      },
      value: categoryValue ? { name: categoryValue } : { name: EMPTY_CATEGORY },
      handleChange: onCategoryChange,
      onOptionCreated: onCategoryCreated,
      useFormik: false,
      customComponents: { MenuList },
    }
  }

  const onCategoryChange = (option, afterLoad) => {
    setValue(option.name)
    onChange && onChange(option ? option.name : '')
    afterLoad && formikContext && formikContext.setFieldValue && formikContext.setFieldValue('category', option.name)
  }

  const onCategoryCreated = (option) => {
    const newCategories = [...categories, option]

    request(settingsConfig.customer, {
      auth: true,
      method: 'POST',
      data: { categories: newCategories },
    })
      .then(({ body: { data: { categories } } }) => {
        toast.showToast({ message: lang.article.customerInput.optionCreated(option) })
        onCategoryChange({ name: option }, true)
        setCategories(categories)
      })
  }

  const startManualEntry = () => {
    setManualValue('')
    setIsManualEntry(true)
  }

  const cancelManualEntry = () => {
    setManualValue('')
    setIsManualEntry(false)
  }

  const confirmManualEntry = () => {
    const trimmed = (manualValue || '').trim()
    if (!trimmed) {
      cancelManualEntry()
      return
    }
    onCategoryCreated(trimmed)
    setManualValue('')
    setIsManualEntry(false)
  }

  const MenuList = (menuProps) => (
    <components.MenuList {...menuProps}>
      <div
        onClick={startManualEntry}
        className={classes.menuListButton}
      >
        + {lang.generics.new}
      </div>
      {menuProps.children}
    </components.MenuList>
  )

  // necessary for refreshing options if a new one was created
  const categorySelectProps = getCategorySelectProps()
  if (isManualEntry) {
    return (
      <TextInput
        name={'category-manual'}
        value={manualValue}
        label={lang.tagForm.customerCategories.type}
        autoFocus
        useFormik={false}
        dataQsId={dataQsId}
        onChange={(val) => setManualValue(val)}
        onEnter={confirmManualEntry}
        onBlur={confirmManualEntry}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault()
            cancelManualEntry()
          }
        }}
        customClass={null}
        className={null}
      />
    )
  }
  return (
    <SelectInput dataQsId={dataQsId} {...categorySelectProps} readOnly={readOnly} />
  )
}

CustomerCategoryInput.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  readOnly: PropTypes.bool,
  useFormik: PropTypes.bool,
  dataQsId: PropTypes.string.isRequired,
}

export default CustomerCategoryInput
